import { useMemo } from "react";
import { Task } from "@common/types/task";
import { useOrganizationCloses } from "@api/close.api";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import type { TaskWorkspaceData } from "@components/tasks/useTaskWorkspaceData";
import {
    TaskMemberOption,
    toTaskMemberOption,
    useCloseLookups,
    useCloseStageLookups,
    useTaskMemberLookups,
} from "@components/tasks/taskWorkspaceDataLookups";

const NO_TASK_GROUP_IDS: string[] = [];
const NO_TASK_GROUPS_MAP = new Map<string, string>();
const NO_ENTITIES_MAP = new Map<number, string>();

const getClientAssigneeOptions = ({
    tasks,
    knownMemberIds,
}: {
    tasks: Task[];
    knownMemberIds: Set<number>;
}): TaskMemberOption[] => {
    const assigneeOptionsById = new Map<number, TaskMemberOption>();
    for (const { assignee } of tasks) {
        if (assignee && !knownMemberIds.has(assignee.id)) {
            assigneeOptionsById.set(assignee.id, {
                id: assignee.id,
                name: assignee.name,
                sortName: assignee.name,
            });
        }
    }
    return [...assigneeOptionsById.values()];
};

export function useOrganizationTaskWorkspaceData(
    tasks: Task[],
): TaskWorkspaceData {
    const { data: members = [] } = useCurrentOrganizationMembers();
    const {
        closeStageIds,
        closeStagesMap,
        hasCloseStages,
        areCloseStagesLoading,
    } = useCloseStageLookups();
    const {
        data: closes = [],
        isFetched: areClosesFetched,
        isFetching: areClosesFetching,
    } = useOrganizationCloses();
    const { closeIds, closesMap, hasCloses } = useCloseLookups(closes);

    const memberOptions = useMemo(() => {
        const organizationMemberOptions = members.map(toTaskMemberOption);
        const clientAssigneeOptions = getClientAssigneeOptions({
            tasks,
            knownMemberIds: new Set(
                organizationMemberOptions.map((member) => member.id),
            ),
        });
        return [...organizationMemberOptions, ...clientAssigneeOptions];
    }, [members, tasks]);

    const { workspaceMemberIds, workspaceMembersMap } =
        useTaskMemberLookups(memberOptions);

    return {
        workspaceMemberIds,
        workspaceMembersMap,
        workspaceTaskGroupIds: NO_TASK_GROUP_IDS,
        workspaceTaskGroupsMap: NO_TASK_GROUPS_MAP,
        closeStageIds,
        closeStagesMap,
        closeIds,
        closesMap,
        entitiesNameMap: NO_ENTITIES_MAP,
        hasCloseStages,
        hasCloses,
        areCloseStagesLoading,
        areClosesFetched,
        areClosesFetching,
    };
}
